import useTranslation from "next-translate/useTranslation";
import React, { FC } from "react";
import { BikeType } from "types";
import { TheCycleverseTheme } from "src/theme";
import { makeStyles } from "@material-ui/core/styles";

import ButtonLink from "../../Navigation/ButtonLink";
import LocaleLink from "../../Navigation/LocaleLink";
import Text from "../Text/Text";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  productBestOverview: {
    margin: theme.spacing(2),
    marginBottom: theme.spacing(6),
    textAlign: "center",
  },
  headline1: {
    ...theme.headline2,
    fontWeight: 300,
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      fontSize: 20,
    },
  },
  headline2: {
    fontSize: 36,
    marginTop: 0,
    fontFamily: theme.highlightFontFamily,
    fontWeight: 900,
    maxWidth: 900,
    display: "inline-block",
    lineHeight: "1.1",
  },
  content: {
    textAlign: "left",
    maxWidth: 500,
    width: "100%",
    display: "inline-block",
    fontSize: "medium",
  },
  intro: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  item: {
    marginBottom: theme.spacing(6),
  },
  itemHeadline: {
    fontFamily: theme.highlightFontFamily,
    margin: 0,
    fontSize: 30,
    lineHeight: 1,
    marginBottom: theme.spacing(1),
  },
  link: {
    textDecoration: "underline",
    lineHeight: 2,
  },
}));

const useBikeFinderStyles = makeStyles((theme: TheCycleverseTheme) => ({
  bikeFinder: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),
    textAlign: "center",
  },
  bikeFinderButton: {
    backgroundColor: theme.saleColor,
    color: theme.palette.primary.main,
  },
  bikeFinderText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  bikeFinderText1: {
    maxWidth: 300,
    display: "inline-block",
  },
}));

const BikeFinderButton: FC<{ flavor: BikeType; translationsNamespace: string }> = ({
  flavor,
  translationsNamespace,
}) => {
  const classes = useBikeFinderStyles();
  const { t } = useTranslation(translationsNamespace);
  return (
    <div className={classes.bikeFinder}>
      <img
        loading="lazy"
        src={
          flavor == "ebike"
            ? "https://images.thecycleverse.com/E-Clara-black.svg"
            : "https://images.thecycleverse.com/clara.svg"
        }
        width={45}
        height={40}
        alt="clara"
      />
      <div className={classes.bikeFinderText}>
        <div className={classes.bikeFinderText1}>{t("productBestBikeBikeFinderText")}</div>
      </div>
      <ButtonLink
        buttonClassName={classes.bikeFinderButton}
        target="new"
        href={t("productBestBikeBikeFinderLink")}
        as={t("productBestBikeBikeFinderLink")}
      >
        {t("productBestBikeBikeFinderButton")}
      </ButtonLink>
    </div>
  );
};

const ProductBestOverview: FC<{ flavor: BikeType; items: any; textId: string; translationsNamespace: string }> = ({
  flavor,
  items,
  textId,
  translationsNamespace,
}) => {
  const classes = useStyles();
  const { t, lang } = useTranslation(translationsNamespace);

  return (
    <div
      className={classes.productBestOverview}
      data-test={`page-beste-${flavor == "biobike" ? "fahrraeder" : "e-bikes"}`}
    >
      <h1 className={classes.headline1}>{t(`productBestBikeHeadline1`)}</h1>
      <h2 className={classes.headline2}>{t("productBestBikeHeadline2")}</h2>

      <div>
        <div className={classes.content}>
          <div className={classes.intro}>{t("productBestBikeIntro")}</div>

          <BikeFinderButton flavor={flavor} translationsNamespace={translationsNamespace} />

          {items.map((item) => {
            return (
              <div className={classes.item} key={item.key}>
                <div>
                  {/* NOTE(kjellski): discuss - <img loading="lazy"/> won't work here without layout change */}
                  <img loading="lazy" src={`/images/bikes/${item.key}.svg`} height={32} alt={item.key} />
                </div>
                <h3 className={classes.itemHeadline}>
                  {t("bestBikesCommons:productBestBikeItemHeadline", { name: item.name })}
                </h3>
                {item.priceSegments.map((price) => {
                  return (
                    <div key={`${item.key}-${price}`}>
                      <LocaleLink
                        notStyled
                        href="/beste/[generic]"
                        as={`/beste/${item.key}-unter-${price}`}
                        target="new"
                        aClass={classes.link}
                        dataTest="best-segment-link"
                      >
                        {t("bestBikesCommons:productBestBikeItemPriceUnder", { name: item.name, price })}
                      </LocaleLink>
                    </div>
                  );
                })}
                <div>
                  <LocaleLink
                    notStyled
                    href="/beste/[generic]"
                    as={`/beste/high-end-${item.key}`}
                    target="new"
                    aClass={classes.link}
                  >
                    {t("bestBikesCommons:productBestBikeItemHighEnd", { name: item.name })}
                  </LocaleLink>
                </div>
              </div>
            );
          })}

          <BikeFinderButton flavor={flavor} translationsNamespace={translationsNamespace} />

          <Text anchorId="info" useCache={true} id={`product-best-overview-${lang}-${textId}`} />
        </div>
      </div>
    </div>
  );
};

export default ProductBestOverview;
